'use client';

import { useEffect, useRef } from 'react';
import type Hls from 'hls.js';

interface LazyHeroVideoProps {
  playbackId: string;
}

const VIDEO_CLASS =
  'pointer-events-none absolute inset-0 z-0 h-full w-full object-cover object-center';

const PLAY_RETRY_MS = 250;
const PLAY_RETRY_MAX_MS = 8000;
const ORIENTATION_RECOVERY_MS = 350;

/** Only static rendition confirmed available for this asset. */
const MUX_MP4_FALLBACK = 'highest.mp4';

function muxHlsUrl(playbackId: string) {
  const params = new URLSearchParams({
    min_resolution: '720p',
    max_resolution: '1080p',
  });
  return `https://stream.mux.com/${encodeURIComponent(playbackId)}.m3u8?${params}`;
}

function muxMp4Url(playbackId: string, rendition: string) {
  return `https://stream.mux.com/${encodeURIComponent(playbackId)}/${rendition}`;
}

function isWebKitSafari() {
  const ua = navigator.userAgent;
  return /Safari/i.test(ua) && !/Chrome|Chromium|CriOS|Edg|OPR|FxiOS/i.test(ua);
}

function isTouchLikeDevice() {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(pointer: coarse)').matches || window.matchMedia('(hover: none)').matches
  );
}

/**
 * Native HLS only where playback is reliable. Chrome returns "maybe" for
 * application/vnd.apple.mpegurl without working native HLS — see hls.js #7827.
 */
export function supportsNativeHlsPlayback() {
  const probe = document.createElement('video');
  const result = probe.canPlayType('application/vnd.apple.mpegurl');
  if (result === 'probably') return true;
  if (result === 'maybe' && isWebKitSafari()) return true;
  return false;
}

/**
 * Force muted background playback — attribute + programmatic play() with retries.
 * @see https://www.mux.com/blog/video-autoplay-considered-harmful
 */
function forceAutoplay(video: HTMLVideoElement) {
  video.muted = true;
  video.defaultMuted = true;
  video.volume = 0;
  video.playsInline = true;
  video.loop = true;
  video.autoplay = true;
  video.setAttribute('muted', '');
  video.setAttribute('playsinline', '');
  video.setAttribute('webkit-playsinline', '');
  video.setAttribute('autoplay', '');

  const attemptPlay = () => {
    if (video.paused && !video.ended) {
      void video.play().catch(() => {
        // Retry on buffer / visibility events below.
      });
    }
  };

  attemptPlay();

  const bufferEvents = [
    'loadstart',
    'loadedmetadata',
    'loadeddata',
    'canplay',
    'canplaythrough',
    'playing',
  ] as const;

  for (const event of bufferEvents) {
    video.addEventListener(event, attemptPlay);
  }

  const onVisible = () => {
    if (!document.hidden) attemptPlay();
  };

  document.addEventListener('visibilitychange', onVisible);
  window.addEventListener('pageshow', attemptPlay);

  const retryInterval = window.setInterval(attemptPlay, PLAY_RETRY_MS);
  const stopInterval = window.setTimeout(() => {
    window.clearInterval(retryInterval);
  }, PLAY_RETRY_MAX_MS);

  return () => {
    window.clearInterval(retryInterval);
    window.clearTimeout(stopInterval);
    document.removeEventListener('visibilitychange', onVisible);
    window.removeEventListener('pageshow', attemptPlay);
    for (const event of bufferEvents) {
      video.removeEventListener(event, attemptPlay);
    }
  };
}

function loadMp4Fallback(video: HTMLVideoElement, playbackId: string) {
  video.src = muxMp4Url(playbackId, MUX_MP4_FALLBACK);
  video.load();
  return () => {};
}

/**
 * Safari → native HLS. Chrome/Firefox/Edge → hls.js, with progressive MP4 fallback.
 */
function loadVideoSource(video: HTMLVideoElement, playbackId: string): () => void {
  const hlsUrl = muxHlsUrl(playbackId);

  if (supportsNativeHlsPlayback()) {
    video.src = hlsUrl;
    video.load();
    return () => {};
  }

  let cancelled = false;
  let hls: Hls | null = null;

  void import('hls.js')
    .then(({ default: Hls }) => {
      if (cancelled) return;

      if (Hls.isSupported()) {
        hls = new Hls({
          enableWorker: true,
          lowLatencyMode: false,
          maxBufferLength: 30,
          startLevel: -1,
        });
        hls.loadSource(hlsUrl);
        hls.attachMedia(video);
        hls.on(Hls.Events.ERROR, (_event, data) => {
          if (!data.fatal || cancelled) return;
          hls?.destroy();
          hls = null;
          loadMp4Fallback(video, playbackId);
        });
        return;
      }

      loadMp4Fallback(video, playbackId);
    })
    .catch(() => {
      if (!cancelled) loadMp4Fallback(video, playbackId);
    });

  return () => {
    cancelled = true;
    hls?.destroy();
    hls = null;
  };
}

/**
 * Full-bleed background `<video>` — no player chrome, no nested poster slot.
 * Poster backstop lives in VideoHero behind this layer.
 */
export function LazyHeroVideo({ playbackId }: LazyHeroVideoProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const allowPauseRef = useRef(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    allowPauseRef.current = false;
    const touchLike = isTouchLikeDevice();

    const stopLoadSource = loadVideoSource(video, playbackId);

    const onPlaying = () => {
      allowPauseRef.current = true;
    };

    video.addEventListener('playing', onPlaying);

    const stopForceAutoplay = forceAutoplay(video);

    let orientationTimer: number | undefined;

    const recoverAfterLayoutChange = () => {
      if (orientationTimer) window.clearTimeout(orientationTimer);
      orientationTimer = window.setTimeout(() => {
        if (!document.hidden && video.paused && !video.ended) {
          void video.play().catch(() => {});
        }
      }, ORIENTATION_RECOVERY_MS);
    };

    window.addEventListener('orientationchange', recoverAfterLayoutChange);
    window.addEventListener('resize', recoverAfterLayoutChange);

    // Touch / Safari: avoid IO pause races during landscape chrome resize; keep hero playing.
    const observer = touchLike
      ? null
      : new IntersectionObserver(
          ([entry]) => {
            if (entry?.isIntersecting) {
              void video.play().catch(() => {});
              return;
            }

            if (allowPauseRef.current) {
              video.pause();
            }
          },
          { threshold: 0 },
        );

    if (observer) {
      observer.observe(video);
    }

    return () => {
      video.removeEventListener('playing', onPlaying);
      stopLoadSource();
      stopForceAutoplay();
      if (orientationTimer) window.clearTimeout(orientationTimer);
      window.removeEventListener('orientationchange', recoverAfterLayoutChange);
      window.removeEventListener('resize', recoverAfterLayoutChange);
      observer?.disconnect();
      video.pause();
      video.removeAttribute('src');
      video.load();
    };
  }, [playbackId]);

  return (
    <video
      ref={videoRef}
      autoPlay
      muted
      loop
      playsInline
      preload="auto"
      disablePictureInPicture
      disableRemotePlayback
      aria-hidden
      className={VIDEO_CLASS}
    />
  );
}
